import { type MouseEvent } from 'react'

import { Info, Play, Square } from 'lucide-react'

import { Playlist } from '@/core/api/entities/playlist/playlist'
import { Schedule } from '@/core/api/entities/schedule'
import { Checkbox } from '@/shared/components/atoms/Checkbox'
import { cn } from '@/shared/utils/helpers'

interface ScheduleRowProps {
	schedule: Schedule
	isSelected: boolean
	onSelectionChange: (checked: boolean) => void
	className?: string
	playlists: Playlist[]
	playingScheduleId: number | null
	playlistIdForSample: number | null
	isLoadingSample: boolean
	currentSong: unknown
	onPlaylistPlayClick: (
		scheduleId: number,
		playlist: Playlist,
		e: MouseEvent<HTMLButtonElement>
	) => void
	disabled?: boolean
	disabledReason?: string
}

export function ScheduleRow({
	schedule,
	isSelected,
	onSelectionChange,
	className,
	playlists,
	playingScheduleId,
	playlistIdForSample,
	isLoadingSample,
	currentSong,
	onPlaylistPlayClick,
	disabled = false,
	disabledReason,
}: ScheduleRowProps) {
	const handleRowClick = () => {
		if (!disabled) {
			onSelectionChange(!isSelected)
		}
	}

	return (
		<div
			className={cn(
				'flex items-center justify-between px-4 py-4',
				disabled
					? 'opacity-50 cursor-not-allowed'
					: 'hover:bg-accent/50 cursor-pointer',
				className
			)}
			onClick={handleRowClick}
		>
			<div className="flex-1 pr-4 flex flex-col justify-center">
				<p className="text-sm font-medium">{schedule.title}</p>
				{schedule.description && (
					<p className="text-xs text-muted-foreground mt-1">
						{schedule.description}
					</p>
				)}
				{disabled && disabledReason && (
					<p className="text-xs text-[color:var(--status-warning)] mt-1 flex items-center gap-1">
						<Info className="h-3 w-3 flex-shrink-0" />
						<span>{disabledReason}</span>
					</p>
				)}

				{/* Thumbnails */}
				<div className="flex lg:gap-3 mt-5 gap-1 lg:px-4 lg:pb-1 overflow-hidden">
					{playlists.slice(0, 3).map((playlist: Playlist) => {
						const isThisPlaylistPlaying =
							playingScheduleId === schedule.id &&
							playlistIdForSample === playlist.id
						const isLoadingThisPlaylist =
							isThisPlaylistPlaying && isLoadingSample

						return (
							<div
								key={playlist.id}
								className="relative aspect-square lg:rounded-2xl rounded-lg bg-gradient-to-br from-zinc-300 to-zinc-400 dark:from-zinc-700 dark:to-zinc-800 flex-shrink-0 flex items-center justify-center overflow-hidden w-16 h-16 group"
							>
								<img
									src={playlist.getSmallestThumbnail()}
									alt={playlist.title}
									className="h-full w-full object-cover"
								/>
								{/* Play/Stop button overlay */}
								<button
									type="button"
									onClick={(e) => onPlaylistPlayClick(schedule.id, playlist, e)}
									disabled={isLoadingThisPlaylist}
									className={`absolute inset-0 flex items-center justify-center bg-black/50 lg:rounded-2xl rounded-lg transition-opacity cursor-pointer disabled:cursor-wait ${
										isThisPlaylistPlaying && currentSong
											? 'opacity-100'
											: 'opacity-0 group-hover:opacity-100'
									}`}
								>
									{isThisPlaylistPlaying ? (
										isLoadingThisPlaylist ? (
											<div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
										) : (
											<Square className="h-5 w-5 text-white fill-white" />
										)
									) : (
										<Play className="h-5 w-5 text-white fill-white" />
									)}
								</button>
							</div>
						)
					})}
				</div>
			</div>
			<Checkbox
				checked={isSelected}
				onCheckedChange={(checked) => onSelectionChange(checked === true)}
				onClick={(e) => e.stopPropagation()}
				disabled={disabled}
			/>
		</div>
	)
}
